import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Space, Button, Typography, Layout } from "antd";
import { ArrowLeftOutlined, LoginOutlined, LogoutOutlined, MoonOutlined, SunOutlined, HistoryOutlined } from '@ant-design/icons';
import { FaMobileAlt } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import Media from "react-media";

const { Title, Text } = Typography;
const { Header: AntHeader } = Layout;

const Header = ({ showAdminLink, showBackToHome, showLogout }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <Media queries={{ small: '(max-width: 576px)' }}>
      {matches => (
        <AntHeader style={{
          backgroundColor: isDark ? '#141414' : '#ffffff',
          borderBottom: `1px solid ${isDark ? '#434343' : '#f0f0f0'}`,
          padding: matches ? '0 16px' : '0 24px',
          height: 'auto',
          lineHeight: 'normal'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: matches ? '16px 0' : '24px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            {/* Logo/Brand */}
            <Link
              to="/"
              style={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Space align="center" size={matches ? "small" : "middle"}>
                {/* Logo image - Vite serves public folder at root */}
                <img
                  src="/logo-werise_ok28cd.png"
                  alt="WeRise Logo"
                  loading="lazy"
                  decoding="async"
                  style={{
                    height: matches ? '32px' : '40px',
                    width: 'auto',
                    objectFit: 'contain',
                    borderRadius: '50%',
                    transition: 'opacity 0.3s ease'
                  }}
                  onLoad={(e) => {
                    e.target.style.opacity = '1';
                  }}
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiM1MmM0MWEiLz4KPHRleHQgeD0iMjAiIHk9IjI2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSJ3aGl0ZSIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPldSPC90ZXh0Pgo8L3N2Zz4K';
                  }}
                />
                <div>
                  <Title level={matches ? 5 : 4} style={{ margin: 0, color: isDark ? '#ffffff' : '#000000d9' }}>
                    WeRise
                  </Title>
                </div>
              </Space>
            </Link>

            {/* Right side actions */}
            <Space size={matches ? "small" : "middle"}>
              {/* Download App Button - visible on main pages */}
              {!showLogout && !user && (
                <a
                  href="/we-rise-v0.2.apk"
                  download="we-rise-v0.2.apk"
                  style={{ textDecoration: 'none' }}
                >
                  <Button
                    type="text"
                    icon={<FaMobileAlt />}
                    style={{
                      color: isDark ? '#a6a6a6' : '#00000073',
                      fontSize: matches ? '14px' : '16px'
                    }}
                    title="Download WeRise App"
                  >
                    {matches ? '' : 'Download App'}
                  </Button>
                </a>
              )}
              
              {/* Survey History Button - visible on main pages */}
              {!showLogout && !user && (
                <Button
                  type="text"
                  icon={<HistoryOutlined />}
                  onClick={() => navigate("/survey/history")}
                  style={{
                    color: isDark ? '#a6a6a6' : '#00000073',
                    fontSize: matches ? '14px' : '16px'
                  }}
                  title="View Survey History"
                >
                  {matches ? '' : 'Survey History'}
                </Button>
              )}

              {/* Theme Toggle Button */}
              <Button
                type="text"
                icon={isDark ? <SunOutlined /> : <MoonOutlined />}
                onClick={toggleTheme}
                style={{
                  color: isDark ? '#a6a6a6' : '#00000073',
                  fontSize: matches ? '14px' : '16px'
                }}
                title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
              />

              {showBackToHome && (
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate("/")}
                  style={{
                    backgroundColor: isDark ? '#262626' : '#f5f5f5',
                    border: `1px solid ${isDark ? '#434343' : '#d9d9d9'}`,
                    color: isDark ? '#ffffff' : '#000000d9',
                    fontSize: matches ? '14px' : '16px'
                  }}
                >
                  {matches ? 'Back' : 'Back to Home'}
                </Button>
              )}

              {showLogout && user && (
                <>
                  <Text type="secondary" style={{ fontSize: matches ? '0.75rem' : '0.875rem', display: matches ? 'none' : 'block' }}>
                    Welcome, {user.email || 'Admin'}
                  </Text>
                  <Button
                    danger
                    icon={<LogoutOutlined />}
                    onClick={handleLogout}
                    style={{ fontSize: matches ? '14px' : '16px' }}
                  >
                    {matches ? '' : 'Logout'}
                  </Button>
                </>
              )}
            </Space>
          </div>
        </AntHeader>
      )}
    </Media>
  );
};

export default Header;